import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { Meta, Title } from '@angular/platform-browser';
import { DOCUMENT, DatePipe } from '@angular/common';
import { FormBuilder, FormGroup, Validators, AbstractControlOptions } from '@angular/forms';
import { Component, OnInit, OnDestroy, ChangeDetectorRef, Renderer2, Inject, PLATFORM_ID } from '@angular/core';
import { catchError, of, takeUntil, take, Subject, combineLatest, map as rxMap, switchMap, Observable } from 'rxjs';
import { isEqual, omit, map, pickBy, identity, sum, reduce, values, groupBy, find, forEach, cloneDeep, sortBy, filter } from 'lodash';

import { ValidationHelper, FormHelper } from '@diktup/frontend/helpers';
import {
  UserRole,
  UserType,
  PaymentType,
  DiscountType,
  OrderTypeEnum,
  FullAddressInput,
  PaymentStatusEnum,
  MarketPlaceOrderWithCartFeesEnum,
} from '@sifca-monorepo/ecommerce-generator';
import {
  StateType,
  CountryType,
  CartProductType,
  ShoppingCartType,
  InstallmentInput,
  DeliveryZonesType,
  OrderSettingsFullType,
  MarketPlaceOrderDtoFeesInput,
} from '@sifca-monorepo/ecommerce-generator';
import { WebsiteIntegrationType } from '@sifca-monorepo/ecommerce-generator';
import { AppService, AuthService, OrdersService, PosService, SeoService, SharedService, UserService } from '../../services';

@Component({
  template: '',
})
export class BaseCheckoutComponent implements OnInit, OnDestroy {
  protected unsubscribeAll: Subject<any> = new Subject<any>();

  seo: any;
  specs: any[];
  totalFees = 0;
  shipIndex = 0;
  billIndex = 0;
  date = new Date();
  initialValues: any;
  totalPrice: number;
  userForm: FormGroup;
  states: StateType[];
  orderForm: FormGroup;
  shippingFees: string;
  couponForm: FormGroup;
  currentUser: UserType;
  authenticated: boolean;
  isButtonDisabled = true;
  countries: CountryType[];
  selectedShipAddress: any;
  priceAfterDiscount: number;
  paymentMethod: PaymentType;
  products: CartProductType[];
  filteredStates: StateType[];
  addingOrder: boolean = false;
  filteredItems: StateType[][];
  isOrderButtonDisabled = false;
  isCouponButtonDisabled = true;
  shoppingCart: ShoppingCartType;
  filteredCountries: CountryType[];
  deliveryZones: DeliveryZonesType[];
  orderSettings: OrderSettingsFullType;
  websiteIntegration: WebsiteIntegrationType;

  constructor(
    protected router: Router,
    protected datepipe: DatePipe,
    protected renderer: Renderer2,
    protected metaTagService: Meta,
    protected titleTagService: Title,
    protected seoService: SeoService,
    protected appService: AppService,
    protected posService: PosService,
    protected formBuilder: FormBuilder,
    protected userService: UserService,
    protected authService: AuthService,
    protected orderService: OrdersService,
    protected translate: TranslateService,
    protected toastrService: ToastrService,
    protected sharedService: SharedService,
    protected changeDetectorRef: ChangeDetectorRef,
    @Inject(DOCUMENT) protected document: Document,
    @Inject(PLATFORM_ID) protected platformId: Object,
  ) {
    const latestDate = this.datepipe.transform(this.date, 'yyyy-MM-dd');
    this.orderForm = this.formBuilder.group({
      notes: [''],
      terms: [false],
      orderTime: [latestDate, Validators.required],
      orderType: [OrderTypeEnum.DELIVERY, Validators.required],
      // paymentMethod: ['bankTransfer', Validators.required],
    });
    this.orderForm.valueChanges.pipe(takeUntil(this.unsubscribeAll)).subscribe((values) => {
      this.isOrderButtonDisabled = false;
    });
    this.orderService.findDeliveryZonesByTarget().subscribe();
    this.orderService.getOrderSettings().subscribe();
    this.seoService
      .getGeneralSEOJSON()
      .pipe(
        take(1),
        catchError(() => {
          return of(null);
        }),
      )
      .subscribe((res) => {
        this.seo = res;
        if (this.seo?.title) {
          this.titleTagService.setTitle(this.seo?.title);
        }
        this.sharedService.tags$ = [...this.metaTagService.addTags(map(this.seo?.metas, (meta) => pickBy(meta, identity))), { charset: 'UTF-8' }];
          this.sharedService.links$ = map(this.seo?.links, (seoLink) => {
            const link = this.renderer.createElement('link');
            if (seoLink.sizes) {
              link.sizes = seoLink.sizes;
            }
            if (seoLink.href) {
              link.href = seoLink.href;
            }
            if (seoLink.rel) {
              link.rel = seoLink.rel;
            }
            this.renderer.appendChild(this.document.head, link);
            return link;
          });
      });
    combineLatest([this.authService.authenticated$, this.posService.allStates$, this.posService.allCountries$])
      .pipe(
        takeUntil(this.unsubscribeAll),
        switchMap(([authenticated, allStates, allCountries]) => {
          let requests: Observable<any>[];
          this.authenticated = authenticated;
          if (authenticated) {
            this.userService.getCurrentUser().subscribe();
            this.orderForm.addControl('paymentMethod', this.formBuilder.control('bankTransfer', Validators.required));
            requests = [of(false), this.orderService.productCart$, this.orderService.orderSettings$, this.userService.currentUser$];
          } else {
            this.appService.loadCart();
            requests = [this.appService.items$, of(false), this.orderService.orderSettings$, of(false)];
          }
          return combineLatest([of(authenticated), of(allStates), of(allCountries), ...requests]);
        }),
        switchMap(([authenticated, allStates, allCountries, items, productCart, orderSettings, user]: any[]) => {
          this.currentUser = user;
          this.userForm = this.formBuilder.group({
            firstName: [this.currentUser?.firstName || '', [Validators.required]],
            lastName: [this.currentUser?.lastName || '', Validators.required],
            email: [this.currentUser?.email || '', [Validators.required, Validators.email]],
            shippingAddress: this.formBuilder.group({
              country: [
                this.currentUser?.shippingAddress?.length && this.currentUser?.shippingAddress[0]?.country?.id
                  ? this.currentUser?.shippingAddress[0]?.country?.id
                  : '',
                Validators.required,
              ],
              state: [
                this.currentUser?.shippingAddress?.length && this.currentUser?.shippingAddress[0]?.state?.id
                  ? this.currentUser?.shippingAddress[0]?.state?.id
                  : '',
                Validators.required,
              ],
              address: [
                this.currentUser?.shippingAddress?.length && this.currentUser?.shippingAddress[0]?.address
                  ? this.currentUser?.shippingAddress[0]?.address
                  : '',
                Validators.required,
              ],
              city: [
                this.currentUser?.shippingAddress?.length && this.currentUser?.shippingAddress[0]?.city
                  ? this.currentUser?.shippingAddress[0]?.city
                  : '',
                Validators.required,
              ],
              postCode: [
                this.currentUser?.shippingAddress?.length && this.currentUser?.shippingAddress[0]?.postCode
                  ? this.currentUser.shippingAddress[0]?.postCode
                  : '',
                Validators.required,
              ],
            }),
            billingAddress: this.formBuilder.group({
              country: [
                this.currentUser.billingAddress?.length && this.currentUser.billingAddress[0]?.country?.id
                  ? this.currentUser.billingAddress[0]?.country?.id
                  : '',
              ],
              state: [
                this.currentUser.billingAddress?.length && this.currentUser.billingAddress[0]?.state?.id
                  ? this.currentUser.billingAddress[0]?.state?.id
                  : '',
              ],
              address: [
                this.currentUser.billingAddress?.length && this.currentUser.billingAddress[0]?.address
                  ? this.currentUser.billingAddress[0]?.address
                  : '',
              ],
              city: [
                this.currentUser.billingAddress?.length && this.currentUser.billingAddress[0]?.city ? this.currentUser.billingAddress[0]?.city : '',
              ],
              postCode: [
                this.currentUser.billingAddress?.length && this.currentUser.billingAddress[0]?.postCode
                  ? this.currentUser.billingAddress[0]?.postCode
                  : '',
              ],
            }),
            phone: this.formBuilder.group(
              {
                number: [this.currentUser.phone?.number || '', Validators.required],
                countryCode: [this.currentUser.phone?.countryCode || orderSettings?.deliveryCountries[0]?.dialCode, Validators.required],
              },
              { validators: [Validators.required] },
            ),
          });
          this.initialValues = this.userForm.value;
          this.userForm
            .get(['shippingAddress', 'country'])
            .valueChanges.pipe(takeUntil(this.unsubscribeAll))
            .subscribe((value) => {
              const groupedByCountry = groupBy(this.orderSettings?.deliveryStates, 'country.id');
              this.filteredItems = values(groupedByCountry);
              this.filteredStates = find(this.filteredItems, (countryArray) => countryArray.some((state) => state.country.id === value));
            });
          if (!authenticated) {
            this.userForm.addControl(
              'pwd',
              this.formBuilder.group(
                {
                  password: [''],
                  confirmPassword: [''],
                },
                { validators: [ValidationHelper.matchingFields('password', 'confirmPassword')] } as AbstractControlOptions,
              ),
              { emitEvent: false },
            );
          }
          if (productCart) {
            this.shoppingCart = productCart;
            this.totalPrice = +productCart?.price?.gross?.afterReduction;
            this.products = productCart?.products;
          }
          if (orderSettings) {
            this.orderSettings = orderSettings;
            if (this.orderSettings.deliveryCountries?.length) {
              this.filteredCountries = this.orderSettings.deliveryCountries;
              if (this.userForm) {
                this.userForm.get(['shippingAddress', 'country']).patchValue(this.filteredCountries[0].id);
              }
            } else if (allStates?.length) {
              this.filteredStates = allStates;
            }
            if (this.orderSettings.deliveryStates?.length) {
            } else if (allCountries?.length) {
              this.filteredCountries = allCountries;
            }
            this.totalFees = sum(map(this.orderSettings?.extraFees, (fees) => +fees?.value));
          }
          if (items?.length) {
            this.products = items;
            this.orderService.selectedSpecs$ = reduce(
              this.products,
              (accumulator, current: any) => {
                return [...accumulator, ...map(current?.product?.specs, 'value')];
              },
              [],
            );
            return this.orderService.calculateShoppingCart(
              map(this.products, (product) => {
                return {
                  barcode: product.barcode.id,
                  quantity: product.quantity,
                };
              }),
            );
          }
          return of(null);
        }),
        catchError(() => {
          return of(null);
        }),
        rxMap((res: any) => {
          if (res) {
            this.shoppingCart = {
              ...res,
              products: this.products,
            };
            this.totalPrice = +res.price.gross.afterReduction;
            this.changeDetectorRef.markForCheck();
          }
        }),
      )
      .subscribe(() => {
        this.changeDetectorRef.markForCheck();
      });

    combineLatest([this.orderService.selectedSpecs$, this.orderService.deliveryZones$, this.posService.pos$])
      .pipe(
        takeUntil(this.unsubscribeAll),
        rxMap(([selectedSpecs, deliveryZones, pos]) => {
          if (pos) {
            this.paymentMethod = pos?.paymentMethods[0];
          }
          if (selectedSpecs?.length && deliveryZones?.length) {
            this.deliveryZones = sortBy(deliveryZones, (zone) => +zone?.extraFees);
            // this.shippingFeesMap = {
            //   S: this.deliveryZones[0]?.extraFees,
            //   M: this.deliveryZones[1]?.extraFees,
            //   L: this.deliveryZones[2]?.extraFees,
            // };
            // const selectedSpec = selectedSpecs[0];
            // this.shippingFees = this.shippingFeesMap[selectedSpec];
          }
        }),
      )
      .subscribe(() => this.changeDetectorRef.markForCheck());
  }

  ngOnInit(): void {
    this.couponForm = this.formBuilder.group({
      code: ['', Validators.required],
    });
    this.couponForm.valueChanges.pipe(takeUntil(this.unsubscribeAll)).subscribe((values) => {
      this.isCouponButtonDisabled = false;
    });
    this.findCountriesAndStatesAndWebsiteIntegration();
  }

  ngAfterViewInit() {
    this.orderService.calculatedShoppingCart$.pipe(takeUntil(this.unsubscribeAll)).subscribe((calculatedShoppingCart) => {
      if (calculatedShoppingCart) {
        this.totalPrice = +calculatedShoppingCart?.price.gross.afterReduction;
      }
    });
  }

  createOrder() {
    this.createMarketPlaceOrder({
      installments: [
        {
          paymentMethod: this.paymentMethod?.id,
          amount: (
            this.totalPrice +
            this.totalFees +
            +(this.orderForm.get('orderType').value === OrderTypeEnum.DELIVERY ? this.shippingFees || 0 : 0)
          ).toString(),
          paymentStatus: PaymentStatusEnum.OPEN,
        },
      ],
      currency: this.websiteIntegration?.multicurrency?.currencies?.[0]?.currency?.id,
      deliveryAddress: {
        ...pickBy(omit(this.currentUser?.shippingAddress?.[0], 'country', 'state', 'location'), identity),
        ...(this.currentUser?.shippingAddress?.[0]?.state ? { state: this.currentUser?.shippingAddress?.[0]?.state?.id } : {}),
        ...(this.currentUser?.shippingAddress?.[0]?.country ? { country: this.currentUser?.shippingAddress?.[0]?.country?.id } : {}),
      },
      fees: { feesType: MarketPlaceOrderWithCartFeesEnum.CUSTOM, amount: this.shippingFees },
    });
  }

  findCountriesAndStatesAndWebsiteIntegration() {
    combineLatest([this.posService.getCountries(), this.posService.findStatesByCountryPagination(), this.posService.getWebsiteIntegrationByTarget()])
      .pipe(
        catchError(() => {
          return of(null);
        }),
      )
      .subscribe(([res, result, response]) => {
        this.countries = res;
        this.states = result;
        this.websiteIntegration = response;
        this.changeDetectorRef.markForCheck();
      });
  }

  isCouponValid() {
    this.orderService
      .isCouponValid(this.couponForm.get('code').value.trim())
      .pipe(
        catchError(() => {
          this.translate.get('SHARED.INVALID_COUPON').subscribe((text: string) => {
            this.toastrService.error(text);
          });
          return of(null);
        }),
      )
      .subscribe((response: any) => {
        if (response?.isValid) {
          this.orderService.selectedCoupon = response?.coupon;
          this.translate.get('SHARED.COUPON_APPLIED').subscribe((text: string) => {
            this.toastrService.success(text);
          });
          this.changeDetectorRef.markForCheck();
        } else {
          this.translate.get('SHARED.INVALID_COUPON').subscribe((text: string) => {
            this.toastrService.error(text);
          });
          this.changeDetectorRef.markForCheck();
        }
      });
  }

  addDiscountToCart() {
    if (this.orderService.selectedCoupon) {
      const input = {
        discountType: this.orderService.selectedCoupon?.discount?.discountType,
        amount: this.orderService.selectedCoupon?.discount?.amount,
      };
      this.orderService
        .addDiscountToCart(this.shoppingCart?.id, input)
        .pipe(
          catchError(() => {
            return of(null);
          }),
        )
        .subscribe((res) => {
          if (res) {
            this.changeDetectorRef.markForCheck();
          }
        });
    }
  }

  checkEmail() {
    if (this.userForm.get('email').value && !this.authenticated) {
      this.authService
        .isLoginForTargetExist(this.userForm.get('email').value)
        .pipe(take(1))
        .subscribe(({ exist }) => {
          if (exist) {
            this.userForm.get('email').setErrors({ alreadyExists: exist });
          }
        });
    }
  }

  checkPhone() {
    if (this.userForm.get('phone').value) {
      this.authService
        .isLoginForTargetExist(null, this.userForm.get('phone').value)
        .pipe(take(1))
        .subscribe(({ exist }) => {
          if (exist && this.userForm.get(['phone', 'number'])?.value !== this.currentUser?.phone?.number) {
            this.userForm.get('phone').setErrors({ alreadyExists: exist });
          }
        });
    }
  }

  isUserFormButtonDisabled() {
    return (
      this.userForm?.invalid ||
      (this.userForm?.valid &&
        this.currentUser?.lastName === this.userForm.value.lastName &&
        this.currentUser?.firstName === this.userForm.value.firstName &&
        this.currentUser?.phone?.countryCode === this.userForm.value.phone?.countryCode &&
        this.currentUser?.phone?.number === this.userForm.value.phone?.number &&
        ((this.currentUser?.shippingAddress?.length &&
          this.currentUser?.shippingAddress[0]?.address === this.userForm.value.shippingAddress?.address &&
          this.currentUser?.shippingAddress[0]?.city === this.userForm.value.shippingAddress?.city &&
          this.currentUser?.shippingAddress[0]?.country?.id === this.userForm.value.shippingAddress?.country &&
          this.currentUser?.shippingAddress[0]?.state?.id === this.userForm.value.shippingAddress?.state &&
          this.currentUser?.shippingAddress[0]?.postCode === this.userForm.value.shippingAddress?.postCode) ||
          (!this.currentUser?.shippingAddress?.length &&
            !this.userForm.value.shippingAddress?.address &&
            !this.userForm.value.shippingAddress?.city &&
            !this.userForm.value.shippingAddress?.country &&
            !this.userForm.value.shippingAddress?.state &&
            !this.userForm.value.shippingAddress?.postCode)) &&
        ((this.currentUser?.billingAddress?.length &&
          this.currentUser?.billingAddress[0]?.address === this.userForm.value.billingAddress?.address &&
          this.currentUser?.billingAddress[0]?.city === this.userForm.value.billingAddress?.city &&
          this.currentUser?.billingAddress[0]?.country?.id === this.userForm.value.billingAddress?.country &&
          this.currentUser?.billingAddress[0]?.state?.id === this.userForm.value.billingAddress?.state &&
          this.currentUser?.billingAddress[0]?.postCode === this.userForm.value.billingAddress?.postCode) ||
          (!this.currentUser?.billingAddress?.length &&
            !this.userForm.value.billingAddress?.address &&
            !this.userForm.value.billingAddress?.city &&
            !this.userForm.value.billingAddress?.country &&
            !this.userForm.value.billingAddress?.state &&
            !this.userForm.value.billingAddress?.postCode)))
    );
  }

  isOrderFormButtonDisabled() {
    return (
      this.orderForm.invalid ||
      this.totalPrice === 0 ||
      this.userForm?.invalid ||
      (this.authenticated && !this.currentUser?.email) ||
      (this.authenticated && !this.currentUser?.lastName) ||
      (this.authenticated && !this.currentUser?.firstName) ||
      (this.authenticated && !this.currentUser?.phone?.countryCode) ||
      (this.authenticated && !this.currentUser?.phone?.number) ||
      (this.authenticated && this.currentUser?.shippingAddress?.length && !this.currentUser?.shippingAddress[0]?.address) ||
      (this.authenticated && this.currentUser?.shippingAddress?.length && !this.currentUser?.shippingAddress[0]?.city) ||
      (this.authenticated && this.currentUser?.shippingAddress?.length && !this.currentUser?.shippingAddress[0]?.country) ||
      (this.authenticated && this.currentUser?.shippingAddress?.length && !this.currentUser?.shippingAddress[0]?.state) ||
      (this.authenticated && this.currentUser?.shippingAddress?.length && !this.currentUser?.shippingAddress[0]?.postCode)
    );
  }

  createMarketPlaceOrder(extraOptions?: {
    installments?: InstallmentInput[];
    currency?: string;
    deliveryAddress?: FullAddressInput;
    fees: MarketPlaceOrderDtoFeesInput;
  }) {
    if (this.orderForm.get('terms').value === false) {
      this.translate.get('SHARED.ACCEPT_TERMS').subscribe((text: string) => {
        this.toastrService.error(text);
      });
      return;
    }
    if (this.isOrderFormButtonDisabled()) {
      const userFormFields = [
        'email',
        'lastName',
        'firstName',
        'phone.number',
        'phone.countryCode',
        'shippingAddress.city',
        'shippingAddress.state',
        'shippingAddress.address',
        'shippingAddress.country',
        'shippingAddress.postCode',
      ];
      forEach(userFormFields, (fieldName) => this.userForm.get(fieldName).markAsDirty());
      return;
    }
    this.addingOrder = true;
    if (this.authenticated) {
      if (this.orderService.selectedCoupon) {
        if (this.orderService.selectedCoupon?.discount?.discountType === DiscountType.AMOUNT) {
          this.priceAfterDiscount = this.totalPrice - +this.orderService.selectedCoupon?.discount?.amount;
        } else {
          this.priceAfterDiscount = +this.totalPrice - (this.totalPrice * +this.orderService.selectedCoupon?.discount?.amount) / 100;
        }
        const input = {
          discountType: this.orderService.selectedCoupon?.discount?.discountType,
          amount: this.orderService.selectedCoupon?.discount?.amount,
        };
        this.orderService
          .addDiscountToCart(this.shoppingCart?.id, input)
          .pipe(
            catchError(() => {
              this.translate.get('SHARED.SOMETHING_WENT_WRONG').subscribe((text: string) => {
                this.toastrService.error(text);
              });
              this.changeDetectorRef.markForCheck();
              return of(null);
            }),
            switchMap(() => {
              const input: any = {
                ...omit(this.orderForm.value, 'terms'),
                ...(extraOptions?.currency ? { currency: extraOptions?.currency } : {}),
                ...(extraOptions?.installments?.length ? { installments: extraOptions?.installments } : {}),
                ...(this.orderSettings?.extraFees?.length ? { extraFees: this.orderSettings?.extraFees } : {}),
                ...(this.orderForm.value.orderType === OrderTypeEnum.DELIVERY && extraOptions?.fees
                  ? {
                      deliveryAddress: extraOptions?.deliveryAddress,
                      fees: extraOptions?.fees,
                    }
                  : {}),
              };
              return this.orderService.createTargetOrderByCart(input);
            }),
            switchMap((res) => {
              if (res) {
                this.totalPrice = 0;
                this.addingOrder = false;
                this.orderService.selectedCoupon = null;
                this.translate.get('SHARED.ORDER_PLACED').subscribe((text: string) => {
                  this.toastrService.success(text);
                });
                this.changeDetectorRef.markForCheck();
                this.router.navigate(['/']);
              }
              return combineLatest([
                this.orderService.sendOrderByEmailAndSms(res.orderId),
                this.orderService.redeemCoupon(this.orderService.selectedCoupon?.couponCode),
              ]);
            }),
          )
          .subscribe();
      } else {
        const input: any = {
          ...omit(this.orderForm.value, 'terms', 'paymentMethod'),
          ...(this.orderForm.value.orderType === OrderTypeEnum.DELIVERY && extraOptions?.fees
            ? {
                deliveryAddress: extraOptions?.deliveryAddress,
                fees: extraOptions?.fees,
              }
            : {}),
          paymentMethod: extraOptions.installments?.[0]?.paymentMethod
            ? extraOptions.installments?.[0]?.paymentMethod
            : this.orderForm.get('paymentMethod').value,
          ...(extraOptions?.currency ? { currency: extraOptions?.currency } : {}),
          ...(extraOptions?.installments?.length ? { installments: extraOptions?.installments } : {}),
          ...(this.orderSettings?.extraFees?.length ? { extraFees: this.orderSettings?.extraFees } : {}),
        };
        this.orderService
          .createTargetOrderByCart(input)
          .pipe(
            catchError(() => {
              this.addingOrder = false;
              this.translate.get('SHARED.SOMETHING_WENT_WRONG').subscribe((text: string) => {
                this.toastrService.error(text);
              });
              this.changeDetectorRef.markForCheck();
              return of(null);
            }),
            switchMap((res) => {
              if (res) {
                this.totalPrice = 0;
                this.addingOrder = false;
                this.orderService.selectedCoupon = null;
                this.appService.items$ = [];
                this.orderService.productCart$ = null;
                this.changeDetectorRef.markForCheck();
                this.translate.get('SHARED.ORDER_PLACED').subscribe((text: string) => {
                  this.toastrService.success(text);
                });
                this.router.navigate(['/']);
              }
              return this.orderService.sendOrderByEmailAndSms(res.orderId);
            }),
          )
          .subscribe();
      }
    } else {
      const input: any = {
        ...omit(this.orderForm.value, 'terms'),
        ...(this.userForm.value.pwd.password ? { roles: UserRole.CONSUMER } : {}),
        // userSubject: 'User created',
        ...(this.userForm.value.pwd.password
          ? { ...omit(this.userForm.value, 'pwd', 'billingAddress') }
          : { ...omit(this.userForm.value, 'pwd', 'billingAddress', 'shippingAddress') }),

        // orderSubject: 'Confirmation de commande : Votre achat a été confirmé !',
        ...(this.userForm.value.pwd.password ? { password: this.userForm.value.pwd.password } : {}),
        ...(this.orderForm.value.orderType === OrderTypeEnum.DELIVERY && extraOptions?.fees
          ? {
              deliveryAddress: {
                ...this.userForm.value.shippingAddress,
                owner: {
                  name: this.userForm.value.firstName + ' ' + this.userForm.value.lastName,
                  phone: this.userForm.value.phone,
                },
              },
              fees: extraOptions?.fees,
            }
          : {}),
        ...(this.orderSettings?.extraFees?.length ? { extraFees: this.orderSettings?.extraFees } : {}),
        ...(extraOptions?.currency ? { currency: extraOptions?.currency } : {}),
        ...(extraOptions?.installments?.length ? { installments: extraOptions?.installments } : {}),
        products: map(this.products, (product) => ({
          quantity: product.quantity,
          barcode: product.barcode.id,
        })),
      };
      if (!this.userForm.value.pwd.password) {
        this.orderService
          .createTargetOrderWithCartForGuestUser(input)
          .pipe(
            catchError(() => {
              this.addingOrder = false;
              this.translate.get('SHARED.SOMETHING_WENT_WRONG').subscribe((text: string) => {
                this.toastrService.error(text);
              });
              this.changeDetectorRef.markForCheck();
              return of(null);
            }),
          )
          .subscribe((order) => {
            if (order) {
              this.totalPrice = 0;
              this.addingOrder = false;
              this.appService.items$ = [];
              this.orderService.productCart$ = null;
              this.translate.get('SHARED.REGISTERED_LOGGED_IN').subscribe((text: string) => {
                this.toastrService.success(text);
              });
              this.router.navigate(['/']);
            }
          });
      } else {
        this.orderService
          .createTargetOrderWithCartAndRegisterUser(input)
          .pipe(
            catchError(() => {
              this.addingOrder = false;
              this.translate.get('SHARED.SOMETHING_WENT_WRONG').subscribe((text: string) => {
                this.toastrService.error(text);
              });
              this.changeDetectorRef.markForCheck();
              return of(null);
            }),
            switchMap((res) => {
              if (res) {
                this.totalPrice = 0;
                this.addingOrder = false;
                this.appService.items$ = [];
                this.orderService.productCart$ = null;
                this.translate.get('SHARED.REGISTERED_LOGGED_IN').subscribe((text: string) => {
                  this.toastrService.success(text);
                });
                this.router.navigate(['/']);
              }
              return this.orderService.sendOrderByEmailAndSms(res.orderId);
            }),
          )
          .subscribe();
      }
    }
  }

  updateInfo() {
    let shipAddresses;
    let billAddresses;
    const shippingAddresses = this.currentUser?.shippingAddress?.length ? cloneDeep(this.currentUser?.shippingAddress) : [];
    const billingAddresses = this.currentUser?.billingAddress?.length ? cloneDeep(this.currentUser?.billingAddress) : [];
    shippingAddresses[this.shipIndex] = this.userForm.value?.shippingAddress;
    billingAddresses[this.billIndex] = this.userForm.value?.billingAddress;
    if (this.shipIndex > 0) {
      const [item] = shippingAddresses.splice(this.shipIndex, 1);
      shippingAddresses.unshift(item);
    }
    if (this.billIndex > 0) {
      const [item] = billingAddresses.splice(this.billIndex, 1);
      billingAddresses.unshift(item);
    }
    shipAddresses = filter(
      map(shippingAddresses, (address) => {
        return {
          ...(address?.country ? { country: address?.country?.id || address?.country } : {}),
          ...(address?.state ? { state: address?.state?.id || address?.state } : {}),
          ...(address?.city ? { city: address?.city } : {}),
          ...(address?.address ? { address: address?.address } : {}),
          ...(address?.postCode ? { postCode: address?.postCode } : {}),
          ...(address?.owner?.name
            ? {
                owner: {
                  name: address?.owner?.name,
                  ...(address?.owner?.phone ? { phone: address?.owner?.phone } : {}),
                },
              }
            : {}),
        };
      }),
      (addr) => Object.keys(addr).length > 0,
    );
    billAddresses = filter(
      map(billingAddresses, (address) => {
        return {
          ...(address?.country ? { country: address?.country?.id || address?.country } : {}),
          ...(address?.state ? { state: address?.state?.id || address?.state } : {}),
          ...(address?.city ? { city: address?.city } : {}),
          ...(address?.address ? { address: address?.address } : {}),
          ...(address?.postCode ? { postCode: address?.postCode } : {}),
          ...(address?.owner?.name
            ? {
                owner: {
                  name: address?.owner?.name,
                  ...(address?.owner?.phone ? { phone: address?.owner?.phone } : {}),
                },
              }
            : {}),
        };
      }),
      (addr) => Object.keys(addr).length > 0,
    );

    const input: any = {
      ...FormHelper.getDifference(
        omit(this.initialValues, ['shippingAddress', 'phone', 'billingAddress']),
        omit(this.userForm.value, ['shippingAddress', 'phone', 'billingAddress']),
      ),
      ...(shipAddresses?.length ? { shippingAddress: shipAddresses } : {}),
      ...(billAddresses?.length ? { billingAddress: billAddresses } : {}),

      ...(isEqual(this.initialValues?.phone ? this.initialValues?.phone : {}, this.userForm.value?.phone ? this.userForm.value?.phone : {})
        ? {}
        : { phone: this.userForm.value?.phone }),
    };
    this.userService
      .updateCurrentUser(input)
      .pipe(
        catchError(() => {
          this.translate.get('SHARED.SOMETHING_WENT_WRONG').subscribe((text: string) => {
            this.toastrService.error(text);
          });
          return of(null);
        }),
      )
      .subscribe((result) => {
        if (result) {
          this.isButtonDisabled = true;
          this.translate.get('SHARED.CHANGES_SAVED').subscribe((text: string) => {
            this.toastrService.success(text);
          });
          this.changeDetectorRef.markForCheck();
        }
      });
  }

  getPaymantMethod(field: string) {
    if (field === 'BANK_TRANSFER' || field === 'CHECK' || field === 'CASH') {
      this.orderForm.get('paymentMethod').patchValue(field);
    }
  }

  addOpacity(event: any) {
    event.target.parentElement.querySelector('label').setAttribute('style', 'opacity: 0');
    event.stopPropagation();
  }

  ngOnDestroy(): void {
    this.unsubscribeAll.next(null);
    this.unsubscribeAll.complete();
  }
}
